import type { CustomElement } from "components/RichTextEditor/RichTextEditor";

import type { FormValues } from "./types";

// Default template content for campaign overview
export const defaultOverview = [
  {
    type: "paragraph",
    children: [
      {
        text: "",
      },
    ],
  },
] as CustomElement[];

// Default template content for campaign brief
export const defaultBrief = [
  {
    type: "paragraph",
    children: [
      {
        text: "The default content for the overview section provides a basic template for a campaign overview. You can edit the overview content as little or as much as you want. You do not have to use the same sections and structure as the default content.",
        italic: true,
      },
    ],
  },
  {
    type: "heading-one",
    children: [
      {
        text: "Deliverables",
      },
    ],
  },
  {
    type: "accordion",
    children: [
      {
        type: "accordion-item",
        isOpen: true,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "[Placeholder Deliverable Title]" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "[Specify the deliverables required for this campaign]",
                    italic: true,
                  },
                ],
              },
              {
                type: "bulleted-list",
                children: [
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Number] x [Platform] [Content Type] (e.g., 2 x Instagram reels)",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "Add additional deliverables as needed",
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    type: "paragraph",
    children: [
      {
        text: "",
      },
    ],
  },
  {
    type: "heading-one",
    children: [
      {
        text: "Content Requirements",
      },
    ],
  },
  {
    type: "accordion",
    children: [
      {
        type: "accordion-item",
        isOpen: true,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "Timeline and Deadlines" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "[Specify key dates and deadlines]",
                    italic: true,
                  },
                ],
              },
              {
                type: "bulleted-list",
                children: [
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "Campaign Start Date: [Date]",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "Content Due Date: [Date]",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "Posting Schedule: [Specify timing requirements]",
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: true,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "FTC Guidelines" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "All content must comply with FTC disclosure requirements for sponsored content and partnerships.",
                  },
                ],
              },
              {
                type: "bulleted-list",
                children: [
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "Include clear disclosure (#ad, #sponsored, #partnership) visible at the beginning of content",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "For video content, include verbal disclosure at the beginning",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "Ensure disclosures are prominent and easily understood by your audience",
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "Brand Guidelines" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "[Add specific brand guidelines and messaging requirements here]",
                    italic: true,
                  },
                ],
              },
              {
                type: "bulleted-list",
                children: [
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Brand voice and tone guidelines]",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Visual requirements - colors, fonts, logo usage]",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Key messaging points to include]",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Hashtags and mentions required]",
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "Content Guidelines" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "[Specify content style, format, and creative direction]",
                    italic: true,
                  },
                ],
              },
              {
                type: "bulleted-list",
                children: [
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Content style requirements - authentic, lifestyle, tutorial, etc.]",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Technical specifications - dimensions, quality, formats]",
                      },
                    ],
                  },
                  {
                    type: "list-item",
                    children: [
                      {
                        text: "[Do's and don'ts for content creation]",
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    type: "paragraph",
    children: [
      {
        text: "",
      },
    ],
  },
  {
    type: "heading-one",
    children: [
      {
        text: "Additional Information",
      },
    ],
  },
  {
    type: "accordion",
    children: [
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          {
            type: "accordion-item-header",
            children: [
              {
                text: "[Additional Section Title]",
                italic: true,
              },
            ],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "[Add any additional requirements, contact information, or campaign-specific details here]",
                    italic: true,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    type: "paragraph",
    children: [
      {
        text: "",
      },
    ],
  },
];

// Default template content for FAQ
export const defaultFaq = [
  {
    type: "heading-one",
    children: [
      {
        text: "FAQ – Campaign Standards & Guidelines",
      },
    ],
  },
  {
    type: "accordion",
    children: [
      {
        type: "accordion-item",
        isOpen: true,
        children: [
          {
            type: "accordion-item-header",
            children: [
              { text: "💬 Do I have to disclose that this is sponsored?" },
            ],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "Yes! Always be transparent with your audience. Use clear disclosures like #ad or #sponsored in a prominent spot (not buried at the end). You’re required to follow FTC guidelines — your followers should always know when you’ve been paid to promote something.",
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "🧠 What should I say in my post?" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "Your content should reflect your real, honest experience with the product or service. Never exaggerate or make claims you can't personally back up. If you're unsure about anything specific to say or not say, check your brief or ask your NeoReach manager!",
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "🚫 Are there things I shouldn’t say?" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "Yes—definitely avoid:",
                    bold: true,
                  },
                  {
                    type: "bulleted-list",
                    children: [
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "False or misleading claims (especially about results, benefits, or competing products)",
                          },
                        ],
                      },
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "Sharing any non-public or confidential info from the brand or NeoReach",
                          },
                        ],
                      },
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "Content that could be seen as offensive, discriminatory, or unsafe",
                          },
                        ],
                      },
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "Anything that could violate platform policies (like TikTok or Instagram’s terms)",
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "📢 Can I use music in my video?" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "Only if it’s royalty-free or licensed. Unfortunately, TikTok/Reels trending sounds or music from Spotify or Apple Music usually aren’t allowed for brand partnerships.",
                  },
                  {
                    type: "paragraph",
                    children: [
                      {
                        text: "",
                      },
                    ],
                  },
                  {
                    type: "paragraph",
                    children: [
                      {
                        text: "Stick to safe options like:",
                        bold: true,
                      },
                      {
                        type: "bulleted-list",
                        children: [
                          // TODO #campaignLanding: add links to the libraries and to Slate types
                          {
                            type: "list-item",
                            children: [
                              {
                                text: "YouTube Audio Library",
                              },
                            ],
                          },
                          {
                            type: "list-item",
                            children: [
                              {
                                text: "TikTok Royalty Free Library",
                              },
                            ],
                          },
                          {
                            type: "list-item",
                            children: [
                              {
                                text: "Epidemic Sound",
                              },
                            ],
                          },
                          {
                            type: "list-item",
                            children: [
                              {
                                text: "Artlist",
                              },
                            ],
                          },
                        ],
                      },
                      {
                        text: "Ask us if you're unsure — if content is flagged for music issues, it might need to be re-edited.",
                        bold: true,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          {
            type: "accordion-item-header",
            children: [
              { text: "💡 Can I say whatever I want about the brand?" },
            ],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "Keep it honest and respectful. You can absolutely be creative and make it your own—but don’t:",
                  },
                  {
                    type: "bulleted-list",
                    children: [
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "Claim things that aren’t true or can’t be proven",
                          },
                        ],
                      },
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "Put down other brands unfairly",
                          },
                        ],
                      },
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "Say anything that would damage the brand’s reputation or create legal issues",
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "accordion-item",
        isOpen: false,
        children: [
          // 📷 What platforms and rules do I need to follow?
          // Each platform (TikTok, Instagram, YouTube, etc.) has its own rules, so you’re responsible for following both:
          // The platform’s guidelines
          // Any brand-specific guidance shared with you in your brief

          // Don’t worry—we’ll help clarify anything if needed.

          {
            type: "accordion-item-header",
            children: [
              { text: "📷 What platforms and rules do I need to follow?" },
            ],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    text: "Each platform (TikTok, Instagram, YouTube, etc.) has its own rules, so you’re responsible for following both:",
                  },
                  {
                    type: "bulleted-list",
                    children: [
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "The platform’s guidelines",
                          },
                        ],
                      },
                      {
                        type: "list-item",
                        children: [
                          {
                            text: "Any brand-specific guidance shared with you in your brief",
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                type: "paragraph",
                children: [
                  {
                    text: "Don’t worry—we’ll help clarify anything if needed.",
                    bold: true,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    type: "paragraph",
    children: [
      {
        text: "",
      },
    ],
  },
] as CustomElement[];

// Helper function to create initial form values
export const createInitialValues = (
  existingBrief?: any,
  existingOverview?: any,
  existingFaq?: any,
): FormValues => {
  return {
    brief: existingBrief || defaultBrief,
    overview: existingOverview || defaultOverview,
    faq: existingFaq || defaultFaq,
    isPreview: false,
  };
};

// Default form values using template content
export const initialValues = createInitialValues();
